import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { DataTable, DataTableToolbar } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';

import { myCasesQueries, type TaskInboxStatus } from '../queries';
import { usePersonNames } from '../hooks/usePersonNames';
import { compareTasksBy, findTaskSortOption } from '../lib/taskInboxSort';
import { TaskInboxRowItem, type OnEditTask } from './TaskInboxRowItem';
import { TaskInboxSkeleton } from './TaskInboxSkeleton';

const PAGE_SIZE = 25;

interface TaskInboxListProps {
  readonly assignee: string;
  readonly status: TaskInboxStatus;
  readonly sortId: string;
  readonly onEdit: OnEditTask;
}

/**
 * Server-paginated task list (PTV2-1236). Sorting is applied client-side to the
 * loaded page (PTV2-1321) — the backend `sort` param is a no-op for now.
 * Changing the filter or page size resets to the first page.
 */
export function TaskInboxList({
  assignee,
  status,
  sortId,
  onEdit,
}: TaskInboxListProps): ReactElement {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [status, size]);

  const query = useQuery(myCasesQueries.tasks({ assignee, status, page, size }));
  const items = useMemo(() => {
    const rows = query.data?.items ?? [];
    return [...rows].sort(compareTasksBy(findTaskSortOption(sortId)));
  }, [query.data, sortId]);

  const personGuids = useMemo<readonly string[]>(
    () =>
      items.flatMap((task) =>
        task.entityType === 'person' && task.entityGuid !== null ? [task.entityGuid] : [],
      ),
    [items],
  );
  const personNames = usePersonNames(personGuids);

  const totalElements = query.data?.totalElements ?? 0;
  const totalPages = query.data?.totalPages ?? 1;
  const from = totalElements === 0 ? 0 : page * size + 1;
  const to = Math.min(totalElements, from + items.length - 1);

  if (query.isPending) return <TaskInboxSkeleton />;

  if (query.isError) {
    return (
      <div className="max-w-xl">
        <FetchErrorCallout
          error={query.error}
          heading={t('routes.myCases.inbox.error.heading')}
          message={t('routes.myCases.inbox.error.message')}
          codeLabel={t('routes.myCases.inbox.error.codeLabel')}
          onRetry={() => {
            query.refetch().catch(() => undefined);
          }}
          retryLabel={t('routes.myCases.inbox.error.retry')}
        />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {t('routes.myCases.inbox.empty')}
      </p>
    );
  }

  return (
    <DataTable aria-label={t('routes.myCases.inbox.listAriaLabel')} className="flex flex-col">
      <DataTableToolbar
        pageSize={size}
        onPageSizeChange={setSize}
        from={from}
        to={to}
        total={totalElements}
        onPagePrevious={() => {
          setPage((p) => Math.max(0, p - 1));
        }}
        onPageNext={() => {
          setPage((p) => Math.min(totalPages - 1, p + 1));
        }}
        canGoPrevious={page > 0}
        canGoNext={page < totalPages - 1}
        pageSizeSelectId="my-cases-page-size"
      />
      <ul className="divide-y divide-border overflow-auto">
        {items.map((task) => (
          <TaskInboxRowItem
            key={task.id}
            task={task}
            entityName={
              task.entityGuid !== null ? (personNames.get(task.entityGuid) ?? null) : null
            }
            onEdit={onEdit}
          />
        ))}
      </ul>
    </DataTable>
  );
}
